import { useEffect, useState } from 'react'
import { Plus, Users, Edit, Trash2 } from 'lucide-react'
import { departmentService } from '../../services/organizationService'
import type { Department } from '../../types'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'

interface DepartmentListProps {
  organizationId?: string
  onEdit?: (dept: Department) => void
  onCreate?: () => void
}

export function DepartmentList({ organizationId, onEdit, onCreate }: DepartmentListProps) {
  const [departments, setDepartments] = useState<Department[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadDepartments()
  }, [organizationId])

  const loadDepartments = async () => {
    try {
      setLoading(true)
      const data = await departmentService.getAll(organizationId)
      setDepartments(data)
      setError(null)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load departments')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this department?')) return

    try {
      await departmentService.delete(id)
      await loadDepartments()
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete department')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">Loading departments...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">{error}</p>
        <Button onClick={loadDepartments} className="mt-2" variant="outline" size="sm">
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold">Departments</h3>
          <p className="text-sm text-muted-foreground">Manage departments within this organization</p>
        </div>
        {onCreate && (
          <Button onClick={onCreate} size="sm">
            <Plus className="w-4 h-4 mr-2" />
            New Department
          </Button>
        )}
      </div>

      {departments.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-8">
            <Users className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No departments yet</p>
            {onCreate && (
              <Button onClick={onCreate} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Create First Department
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {departments.map((dept) => (
            <Card key={dept.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary" />
                    <CardTitle className="text-base">{dept.name}</CardTitle>
                  </div>
                  {dept.department_type && (
                    <Badge variant="secondary">
                      {dept.department_type}
                    </Badge>
                  )}
                </div>
                {dept.description && (
                  <CardDescription className="text-sm">{dept.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {dept.manager_name && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Manager:</span>
                      <span className="font-medium">{dept.manager_name}</span>
                    </div>
                  )}
                  {dept.employee_count !== null && dept.employee_count !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Employees:</span>
                      <span className="font-medium">{dept.employee_count}</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-4">
                  {onEdit && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(dept)}
                      className="flex-1"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(dept.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
